import { AddressTable, Link, LinkList, NodeList, Meta } from '@graph/models';
import getReverseTxType from '../../helpers/getReverseTxType';
import { GraphCustomColor, Transaction } from '@graph/modules';
import { EventName, useCurrencyListStore, useGraphStore } from '@graph/libs';
import { useCallback, useEffect } from 'react';
import { Maybe } from '@apolloGenerated';
import { useGraphAnnotation } from '@graph/libs/hooks';
import { NetworkIconEnum } from '@shared/ui';

export const useGraphLineController = () => {
  const { onAddLineAnnotation } = useGraphAnnotation();
  const { event, sendEvent } = useGraphStore();
  const { graphCurrentCurrency } = useCurrencyListStore();

  useEffect(() => {
    switch (event?.type) {
      case EventName.MENU_CHANGE_LINK_COLOR: {
        const { source, target, color } = event.params!;
        handleChangeLinkColor(source, target, color);
        break;
      }
      case EventName.MENU_LINK_ADD_ANNOTATION: {
        const { source, target } = event.params!;
        onAddLineAnnotation(LinkList.getLinkByHashes(source, target).hash);
        break;
      }
    }
  }, [event]);

  useEffect(() => {
    const links = LinkList.getLinksForGraph(Meta.current.hash);
    links.forEach((item) => {
      handleChangeLinkColorByToken(
        String(item.source),
        String(item.target),
        item?.amount?.map((item) => item.token),
      );
    });
  }, [graphCurrentCurrency, Meta.current?.hash]);

  const handleChangeLinkColorByToken = useCallback(
    (source: string, target: string, token?: string[]) => {
      handleChangeLinkColor(source, target, '#727781' as Maybe<GraphCustomColor>);

      const hasToken = token?.some((item) => item === graphCurrentCurrency);

      if (graphCurrentCurrency !== NetworkIconEnum.Usd && !hasToken) {
        handleChangeLinkColor(source, target, '#E0E0E0' as Maybe<GraphCustomColor>);
      }
    },
    [graphCurrentCurrency],
  );

  const handleChangeLinkColor = useCallback(
    (source: string, target: string, color: Maybe<GraphCustomColor>) => {
      const link = LinkList.getLinkByHashes(source, target);
      link.setCustomColor(color);
      sendEvent({
        type: EventName.DATA_UPDATE,
      });
    },
    [sendEvent],
  );

  const addLink = useCallback(
    (item: Transaction) => {
      const targetHash = item.targetAddress!;
      const sourceHash = item.sourceAddress!;
      const linkItem = LinkList.getLinkByHashes(targetHash, sourceHash);

      if (linkItem) {
        linkItem.sumLink(item);
      } else {
        const linkItem = new Link(targetHash, sourceHash, { token: item.token });

        LinkList.addLink(linkItem);
        linkItem.addAmount(item.txid, +item.amount, item.type, item.token);

        handleChangeLinkColorByToken(sourceHash, targetHash, [item.token]);
      }
    },
    [graphCurrentCurrency],
  );

  const removeLink = useCallback((item: Transaction) => {
    const linkItem = LinkList.getLinkByHashes(item.sourceAddress!, item.targetAddress!);
    if (!linkItem) return;
    if (linkItem.checkCanDeleted()) LinkList.removeLink(linkItem.hash);
    else linkItem.minusLink(item);
  }, []);

  const updateLine = useCallback(
    (value: boolean, item: Transaction) => {
      const targetAddress = item.targetAddress!;
      const sourceAddress = item.sourceAddress!;
      const transactionSourceHash = AddressTable.getCheckedItem(
        item.txid,
        getReverseTxType(item.type),
        targetAddress,
        sourceAddress,
      );
      const transactionTargetHash = AddressTable.getCheckedItem(
        item.txid,
        item.type,
        sourceAddress,
        targetAddress,
      );
      // Добавляем или удаляем связь
      if (value) {
        addLink(item);
        // Устанавливаем чекбокс в текущей таблице
        NodeList.getTableDataByHash(targetAddress).addChecked(transactionSourceHash);
        // Устанавливаем чекбокс в таргет таблице
        NodeList.getTableDataByHash(sourceAddress).addChecked(transactionTargetHash);
        return;
      }
      removeLink(item);
      // Удаляем чекбокс в текущей таблице
      NodeList.getTableDataByHash(targetAddress).removeChecked(transactionSourceHash);
      // Удаляем чекбокс в таргет таблице
      NodeList.getTableDataByHash(sourceAddress).removeChecked(transactionTargetHash);
    },
    [addLink, removeLink],
  );

  return {
    updateLine,
    removeLink,
    addLink,
  };
};
